import * as React from 'react';

import {useNavigate} from 'react-router-dom';
import { useMessage } from '../context/MessageProvider';
import {useLoaderData} from 'react-router-dom';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import TimerIcon from '@mui/icons-material/Timer';

import {useForm, FormProvider} from 'react-hook-form';
import Form from '../components/form/Form';
import FormButton from '../components/form/FormButton';
import FormMultiSelectChipsFilter from '../components/form/FormMultiSelectChipsFilter';
import FormSlider from '../components/form/FormSlider';


import Header from "../components/Header";

import { listAnimals } from '../models/animals';
import { getAssessment } from '../models/assessments';
import { newSectionA, resetSectionARecord, getSectionA, saveSectionA, saveSectionALocal } from '../models/sectiona';

import {today} from "../utils/datetime";

import { findModel } from '../utils/model';

import EwpButton from '../components/EwpButton';
import GoBack from '../components/GoBack';

import CustomTooltip from '../components/tooltip/CustomTooltip';
import HelpIcon from '@mui/icons-material/Help';
import PageAwareManual from '../components/PageAwareManual';

import Timer from '../components/Timer';


import {useLayout} from '../components/layout/Layout';
import { useConfirmationDialog } from '../context/ConfirmationDialogContext';

import { useConnectivity } from '../context/ConnectivityProvider';
import { useTimer } from '../context/TimerProvider';

import { otherAnimalsNearby } from '../utils/assessments';
import { name as speciesName } from '../utils/species';

const CustomSlider = ({name,label,leftLabel,rightLabel,tooltipKey,...props}) => {
    return (
        <FormSlider
            name={name}
            label={<Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                    <Typography color="secondary" sx={{fontWeight: "bold" }}>{label}</Typography>
                    <CustomTooltip keyString={tooltipKey}>
                        <HelpIcon />
                    </CustomTooltip>
                </Stack>}

            min={0}
            max={10}
            step={0.1}
            marks={[
                {
                    value: 0,
                    label: leftLabel,
                },
                {
                    value: 10,
                    label: rightLabel,
                },
            ]}
        {...props}
        />
    );
}

export async function loader({params}) {
    const assessment = await getAssessment(params.assessmentId);
    const animals = await listAnimals();
    const sectiona = await getSectionA(params.sectionAId);

    return {assessment, animals, sectionaRecord: sectiona};
}

export default function AssessmentSectionAFormPage({title = "EWP",...props}) {

    const {setSuccess, setError, setMessage} = useMessage();
    const navigate = useNavigate();
    const {confirm} = useConfirmationDialog();
    const {hideNavigation,showNavigation, smAndLarger, isNavigationShown} = useLayout();
    const {isOnline} = useConnectivity();

    const {isRunning, startTimer, endTimer} = useTimer();

    const {assessment, animals, sectionaRecord} = useLoaderData();
    const [sectiona, setSectiona] = React.useState(sectionaRecord);

    const [working,setWorking] = React.useState(false);


    //console.log("assessment: ", assessment, "animals:", animals);
    //console.log("sectiona: ",sectiona);

    const animal = findModel(animals || [],assessment?.animal_id);

    //console.log("sectiona animal: ", animal);

    const speciesFilter = (animals || []).reduce((acc, cur) => {
        if (!acc.includes(cur.species)) acc.push(cur.species);
        return acc;
    }, []).map((cur) => ({ value: cur, label: speciesName(cur) }));

    //console.log("sectiona species filter: ", speciesFilter);

    // if you're going to do a form submission, this is your starting point for the submit behavior
    const onSubmit = async (inputData) => {
        try {
            setWorking(true);
            showNavigation();

            const payload = {...inputData, submitted_on: today()};
            if(inputData['other_animals_nearby'].includes('None')){
                payload['other_animals_nearby'] = [];
            }

            console.log("section a payload", payload);
            const saveResult = await saveSectionA(payload,isOnline);
            console.log("sectiona save: ", saveResult);

            setSuccess("Section Data Saved");
            navigate("/assessments/" + assessment.id);

        } catch (error) {
            console.error(error);
            setError(error);
        } finally {
            setWorking(false);
        }
    }

    const onChange = async (inputData) => {
        //console.log("onChange write");
        const saveResult = await saveSectionALocal({...sectiona, ...inputData});
        console.log(saveResult);
    }


    const [showBackArrow,setShowBackArrow] = React.useState(true);
    const [showForm,setShowForm] = React.useState(false);


    const timerSeconds = assessment?.section_a_timer_seconds || 60;

    const toggleTimer = async () => {
        if (!isRunning) {
            // starts the timer
            startTimer(timerSeconds)
            hideNavigation();
            setShowBackArrow(false);
            // show the main form
            setShowForm(true);
            // set record.observed_on to the curren timestamp locally
            setSectiona({...sectiona, observed_on: today()});

        } else {
            // prompt to confirm
            confirm({
                title: "Reset Observation?",
                message: "Confirming will cause the timer to stop and reset, but this also resets the observation data for this observation",
                onSubmit: async () => {
                    // reset the record
                    const temp = resetSectionARecord(sectiona);
                    setSectiona({...temp});
                    onChange(temp);

                    // hides the form
                    setShowForm(false);

                    // reset the timer
                    resetTimer();
                }
            })
        }
    }

    const resetTimer = () => {
        showNavigation();
        setShowBackArrow(true);
        endTimer();
    }

    const timerFinished = () => {
        setMessage("Timer Finished");
        resetTimer();
    }

    const defaultValues = {...newSectionA()};
    const formProps = { defaultValues, values: {...sectiona} };
	const methods = useForm(formProps);


    // note you can play with the container maxwidth to spread out the window content appears in
    return (
        <Container component="main" maxWidth="sm">
            {!isNavigationShown && (
                <PageAwareManual
                    variant="fab"
                    text="Page Manual"
                    fabSx={{
                        position: "fixed",
                        left: 16,
                        bottom: 16,
                        zIndex: (theme) => theme.zIndex.appBar + 1,
                    }}
                />
            )}
            {showBackArrow && <GoBack onClick={()=>navigate(`/assessments/${assessment.id}`)} />}
            <Header label={title} />

            <Grid container spacing={2}>
                <Grid xs={6} sm={6}>
                    <Typography>{assessment?.assessment_title || animal.name}</Typography>
                    <Typography>{sectiona.observation_range}</Typography>
                </Grid>
                <Grid xs={6} sm={6}>
                    <EwpButton onClick={toggleTimer}>
                        {(isRunning) ? <Timer onCompletion={timerFinished} /> : <TimerIcon />}
                    </EwpButton>
                </Grid>
                <Grid xs={6} sm={6}>
                </Grid>
            </Grid>

            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)} onChange={onChange}>
                    <Grid container spacing={2} sx={{pr: (!smAndLarger) ? 3 : 0}}>


                        <Grid xs={12} sm={12}>
                            <FormMultiSelectChipsFilter
                                enableSelectNone
                                enableSelectAll
                                name="other_animals_nearby"
                                label="Other Animals Nearby"
                                list={otherAnimalsNearby(animals, animal)}
                                enableTextFilter={true}
                                enableSelectFilter={true}
                                selectFilterList={speciesFilter}
                                defaultSelectFilter={animal?.species}
                            />
                        </Grid>

                        {!showForm && (
                        <Grid xs={12} sm={12}>
                            <Typography variant="body2" color="text.secondary">
                                For best results, use this interface in landscape mode.
                            </Typography>
                        </Grid>
                        )}

                        {showForm && <>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="relaxed" label="Relaxed" leftLabel="Not at all relaxed" rightLabel="Completely relaxed" tooltipKey="assessments.sectiona.terms.relaxed" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="fearful" label="Fearful" leftLabel="Not at all feaful" rightLabel="Complete fearful" tooltipKey="assessments.sectiona.terms.fearful" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="playful" label="Playful" leftLabel="Not at all playful" rightLabel="Completely playful" tooltipKey="assessments.sectiona.terms.playful" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="indifferent" label="Indifferent" leftLabel="Not at all indifferent" rightLabel="Completely indifferent" tooltipKey="assessments.sectiona.terms.indifferent" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="sociable" label="Sociable" leftLabel="Not at all sociable" rightLabel="Completely sociable" tooltipKey="assessments.sectiona.terms.sociable" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="tense" label="Tense" leftLabel="Not at all tense" rightLabel="Completely tense" tooltipKey="assessments.sectiona.terms.tense" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="lively" label="Lively" leftLabel="Not at all lively" rightLabel="Completely lively" tooltipKey="assessments.sectiona.terms.lively" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="withdrawn" label="Withdrawn" leftLabel="Not at all withdrawn" rightLabel="Completely withdrawn" tooltipKey="assessments.sectiona.terms.withdrawn" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="comfortable" label="Comfortable" leftLabel="Not at all comfortable" rightLabel="Completely comfortable" tooltipKey="assessments.sectiona.terms.comfortable" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="irritable" label="Irritable" leftLabel="Not at all irritable" rightLabel="Completely irritable" tooltipKey="assessments.sectiona.terms.irritable" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="at_ease" label="At Ease" leftLabel="Not at all at ease" rightLabel="Completely at ease" tooltipKey="assessments.sectiona.terms.at_ease" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="frustrated" label="Frustrated" leftLabel="Not at all frustrated" rightLabel="Completely frustrated" tooltipKey="assessments.sectiona.terms.frustrated" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="calm" label="Calm" leftLabel="Not at all calm" rightLabel="Completely calm" tooltipKey="assessments.sectiona.terms.calm" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="listless" label="Listless" leftLabel="Not at all listless" rightLabel="Completely listless" tooltipKey="assessments.sectiona.terms.listless" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="curious" label="Curious" leftLabel="Not at all curious" rightLabel="Completely curious" tooltipKey="assessments.sectiona.terms.curious" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="wary" label="Wary" leftLabel="Not at all wary" rightLabel="Completely wary" tooltipKey="assessments.sectiona.terms.wary" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="agitated" label="Agitated" leftLabel="Not at all agitated" rightLabel="Completely agitated" tooltipKey="assessments.sectiona.terms.agitated" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="content" label="Content" leftLabel="Not at all content" rightLabel="Completely content" tooltipKey="assessments.sectiona.terms.content" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="anxious" label="Anxious" leftLabel="Not at all nervous" rightLabel="Completely nervous" tooltipKey="assessments.sectiona.terms.anxious" />
                        </Grid>

                        <Grid xs={12} sm={12} sx={{display: "flex", justifyContent: "center"}}>
                            <CustomSlider name="excited" label="Enthusiastic" leftLabel="Not at all enthusiastic" rightLabel="Completely enthusiastic" tooltipKey="assessments.sectiona.terms.enthusiastic" />
                        </Grid>


                        <Grid xs={12}>
                            <FormButton working={working}>Submit</FormButton>
                        </Grid>
                        </>}

                    </Grid>

                </Form>
            </FormProvider>
        </Container>
    );
}
